// src/utils/session.js
//
// Centralizes every localStorage-based auth/session read+write. This
// replaces the same handful of `localStorage.getItem("access_token")` /
// `JSON.parse(localStorage.getItem("user"))` / viewer-email-fallback
// snippets that had been copy-pasted independently across api/client.js,
// ProtectedRoute.jsx, ProtectedOutlet.jsx, Dashboard.jsx, LoginPage.jsx,
// SsoCallback.jsx, RagSearchPage.jsx, Sowanalysisviewerpage.jsx, and
// SowTestAnalysisPage.jsx.
//
// This is also the seam that will need to change once the planned two-token
// auth model ships (docs/concepts/two-token-auth.md — not live yet): that
// JWT carries org_id/roles/permissions claims, which would be decoded here
// (via the already-installed `jwt-decode`) instead of trusting a separately
// cached `user` object — every caller below stays untouched.

const TOKEN_KEY = "access_token";
const USER_KEY = "user";
const LAST_LOGIN_EMAIL_KEY = "last_login_email";
const LOGIN_ERROR_KEY = "login_error";

export function getAccessToken() {
  return localStorage.getItem(TOKEN_KEY);
}

export function setAccessToken(token) {
  localStorage.setItem(TOKEN_KEY, token);
}

export function isAuthenticated() {
  return Boolean(getAccessToken());
}

export function getUser() {
  try {
    return JSON.parse(localStorage.getItem(USER_KEY) || "null");
  } catch {
    return null;
  }
}

export function setUser(user) {
  localStorage.setItem(USER_KEY, JSON.stringify(user));
}

/** Stores the token + user from a completed login, and remembers the email for next time. */
export function setSession(token, user) {
  setAccessToken(token);
  setUser(user);
  if (user?.email) localStorage.setItem(LAST_LOGIN_EMAIL_KEY, user.email);
}

export function clearSession() {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
}

function normalizePerms(source) {
  if (!Array.isArray(source)) return [];
  return source
    .map((p) => (typeof p === "string" ? p : p?.name))
    .filter(Boolean)
    .map((s) => s.toLowerCase());
}

export function getPermissions() {
  const user = getUser();
  return normalizePerms(user?.role?.permissions || user?.permissions || []);
}

export function hasPermission(key) {
  return getPermissions().includes(String(key).toLowerCase());
}

export function hasAnyPermission(keys = []) {
  return keys.some((k) => hasPermission(k));
}

/** Best-effort viewer email: explicit user_email/email keys, then the cached user object. */
export function getViewerEmail() {
  const direct = (localStorage.getItem("user_email") || localStorage.getItem("email") || "").trim();
  if (direct) return direct.toLowerCase();

  try {
    const user = JSON.parse(localStorage.getItem(USER_KEY) || localStorage.getItem("currentUser") || "null");
    if (user?.email) return String(user.email).trim().toLowerCase();
  } catch {
    // ignore malformed cached user data
  }
  return "";
}

export function getLastLoginEmail() {
  return localStorage.getItem(LAST_LOGIN_EMAIL_KEY) || "";
}

export function setLastLoginEmail(email) {
  if (email) localStorage.setItem(LAST_LOGIN_EMAIL_KEY, email);
}

/** Reads and clears the one-shot login error message shown on the login page. */
export function consumeLoginError() {
  const err = localStorage.getItem(LOGIN_ERROR_KEY) || "";
  if (err) localStorage.removeItem(LOGIN_ERROR_KEY);
  return err;
}

export function setLoginError(message) {
  localStorage.setItem(LOGIN_ERROR_KEY, message);
}
